/**
 * Authentication API Methods
 * Handles login, logout, signup, and password reset functionality
 */

import { apiRequest } from "./apiRequest.js";

export const authMethods = {
  /**
   * Login user with email and password
   */
  login: async (email, password) => {
    const result = await apiRequest("/login/", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    return result;
  },

  /**
   * Login for OAuth2 token (alternative endpoint)
   */
  loginToken: async (username, password) => {
    return await apiRequest("/login/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({ username, password }),
    });
  },

  /**
   * Login with Google OAuth token
   */
  loginWithGoogle: async (googleToken) => {
    const result = await apiRequest("/login/google", {
      method: "POST",
      body: JSON.stringify({ google_token: googleToken }),
    });

    return result;
  },

  /**
   * Logout user
   */
  logout: async (token) => {
    return await apiRequest("/logout/", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  /**
   * Check if username is available
   */
  checkUsername: async (username) => {
    return await apiRequest(
      `/signup/check-username?username=${encodeURIComponent(username)}`,
      {
        method: "GET",
      },
    );
  },

  /**
   * Check if email is available
   */
  checkEmail: async (email) => {
    return await apiRequest(
      `/signup/check-email?email=${encodeURIComponent(email)}`,
      {
        method: "GET",
      },
    );
  },

  /**
   * Send OTP to email for registration
   */
  sendOTP: async (email) => {
    return await apiRequest("/signup/send-otp", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Verify OTP and create user account
   */
  verifyOTPAndCreateAccount: async ({
    username,
    fullName,
    email,
    password,
    otp,
    profileImage,
  }) => {
    const formData = new FormData();

    formData.append("username", username?.trim() || "");
    formData.append("full_name", fullName?.trim() || "");
    formData.append("email", email?.trim() || "");
    formData.append("password", password || "");
    formData.append("otp", otp?.trim() || "");

    if (profileImage?.file) {
      formData.append("avatar", profileImage.file);
    }

    return await apiRequest("/signup/verify-otp", {
      method: "POST",
      body: formData,
    });
  },

  /**
   * Request password reset
   */
  requestPasswordReset: async (email) => {
    return await apiRequest("/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Verify password reset token
   */
  verifyResetToken: async (token) => {
    return await apiRequest(
      `/password-reset/verify-token?token=${encodeURIComponent(token)}`,
    );
  },

  /**
   * Confirm password reset with new password
   */
  confirmPasswordReset: async ({ token, password, confirm_password }) => {
    return await apiRequest("/password-reset/confirm", {
      method: "POST",
      body: JSON.stringify({ token, password, confirm_password }),
    });
  },

  /**
   * Get API information (health check)
   */
  getApiInfo: async () => {
    return await apiRequest("/");
  },
};
